/**
 * Binds the SignNow form-page event contract — MEP-1371.
 *
 * Everything is delegated from `document` rather than bound per element. Legacy binds
 * per element from a lazily loaded bundle and pays for it: on form pages the handlers
 * only exist once `form-page.js` has loaded, so a visitor's very first tap is not
 * counted at all (SLP-2350). Delegation removes that race, which matters most on the
 * traffic that matters most — mobile and tablet were exact zeros on the ME arm.
 *
 * The gates below are load bearing. This runs from the library-wide global script, so
 * it executes on USLegal and DocHub pages too, and `js-editor-link` in particular is a
 * class USLegal components also render. Both gates are evaluated per click rather than
 * once at install time: the global script can run before the markup it inspects is in
 * place, and by the time anyone clicks, it is.
 */

import {
  onCEGetFormClick,
  onCELeadformClick,
  onCeFreeTrialClick,
  onCeFreeTrialSubmit,
} from './analytics-events';

/** Set by Herald on every render; the cheapest reliable product marker on the page. */
const SIGNNOW_BODY_CLASS = 'signnow';

/**
 * Present only on form pages, which is the page type legacy binds these events on
 * (`pages/form/index.js`, `pages/form/gov.js`). Matching on it keeps get-form off
 * SignNow page types whose legacy bundle never emitted it.
 */
const FORM_PAGE_MARKER = 'sn-popup-form-loader';

/** Legacy's get-form target is `.button.js-editor-link`; ME renders `me-button`, so the class alone. */
const GET_FORM_SELECTOR = '.js-editor-link';

const LEADFORM_SELECTOR = [
  "[href*='/contact']",
  "[data-micromodal-trigger='modal-form']",
  "[data-micromodal-trigger='modal-form-business-cloud']",
].join(', ');

/** Legacy scans `.button, .link`; the ME equivalents are `me-button` and plain anchors. */
const FREE_TRIAL_SELECTOR = '.me-button, a';
const FREE_TRIAL_WORDS = ['start', 'free', 'trial'];

/** Legacy excludes this page type from `CE_freeTrial_click`. */
const FREE_TRIAL_EXCLUDED_PATH = '/fill-out-and-edit-pdf';

const isSignNowPage = (): boolean => !!document.body?.classList.contains(SIGNNOW_BODY_CLASS);

const isFormPage = (): boolean => !!document.querySelector(FORM_PAGE_MARKER);

/**
 * Legacy decides this from `innerHTML` at bind time. Reading `textContent` at click
 * time is the same test on the same content, minus a scan of every button on load —
 * these pages are SEO-critical and the scan bought nothing.
 */
const isFreeTrialCta = (element: Element): boolean => {
  if (element.getAttribute('type') === 'submit') return false;
  const label = (element.textContent ?? '').toLowerCase();
  return FREE_TRIAL_WORDS.some(word => label.includes(word));
};

const handleClick = (event: MouseEvent): void => {
  const target = event.target as Element | null;
  if (!target?.closest || !isSignNowPage()) return;

  // Independent checks, not a chain: legacy binds these separately, so one element can
  // legitimately be both a get-form CTA and a trial CTA and emit both events.
  if (isFormPage() && target.closest(GET_FORM_SELECTOR)) {
    onCEGetFormClick();
  }

  if (target.closest(LEADFORM_SELECTOR)) {
    onCELeadformClick();
  }

  const trialCta = target.closest(FREE_TRIAL_SELECTOR);
  if (trialCta && !window.location.pathname.startsWith(FREE_TRIAL_EXCLUDED_PATH) && isFreeTrialCta(trialCta)) {
    onCeFreeTrialClick();
  }
};

/**
 * `CE_freeTrial_submit` — the footer trial form. Legacy keys on an `action` containing
 * `purchase` and reads the email out of the form; the `type="api"` hidden input marks
 * the API-sandbox variant, which belongs to `CE_apiSandbox_submit` and is out of scope
 * for form pages.
 */
const handleSubmit = (event: Event): void => {
  const form = event.target as HTMLFormElement | null;
  if (!form || form.tagName !== 'FORM' || !isSignNowPage()) return;

  const action = form.getAttribute('action') ?? '';
  if (!action.includes('purchase')) return;
  if (form.querySelector('input[type="hidden"][name="type"][value="api"]')) return;

  const email = form.querySelector<HTMLInputElement>('input[type="email"]')?.value;
  if (!email) return;

  onCeFreeTrialSubmit(email);
};

export const installSignNowAnalytics = (): void => {
  document.addEventListener('click', handleClick);
  document.addEventListener('submit', handleSubmit);
};
